import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getClient, getUsers, getActivities, getFollowUps, getTasks, updateFollowUp, 
  getProducts, getSIPs, getDocuments, getNotes, getFamilies,
  createActivity, createFollowUp, createTask, createNote, createSIP, updateSIP, deleteSIP, uploadDocument,
  getHealthInsurances, createHealthInsurance, deleteHealthInsurance, uploadHealthInsuranceDoc,
  getTermInsurances, createTermInsurance, deleteTermInsurance, uploadTermInsuranceDoc,
  getDocumentUrl, deleteDocument, updateTask, toggleClientIndividual, getClientMappedEmployees
} from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { 
  formatDate, formatPhone, maskPAN, getStatusBadgeClass, getPriorityBadgeClass,
  formatApiError, formatCurrency, isMinor, getClientDisplayName 
} from '../../lib/utils';
import { 
  ACTIVITY_TYPES, FOLLOW_UP_TYPES, TASK_TYPES, PRIORITIES, DOCUMENT_TYPES,
  TASK_CATEGORY_MAP, TASK_CATEGORY_LABELS, TASK_STATUSES, MANDATE_TASK_TYPES,
  MANDATE_STATUSES, AMC_NAMES, REJECTION_REASONS, getTaskCategory, TIME_SLOTS, FOLLOWUP_TIME_SLOTS,
  BUSINESS_TIME_SLOTS, CALL_STATUSES, MEETING_STATUSES, CALL_DURATIONS, MEETING_DURATIONS
} from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { TimePicker } from '../../components/ui/time-picker';
import { DatePicker } from '../../components/ui/date-picker';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Pencil, Phone, Envelope, IdentificationCard, CalendarBlank, 
  MapPin, Plus, ClipboardText, CalendarCheck, ChatCircle, Upload, Folder,
  UsersThree, Tag, Note, Clock, ShieldCheck, Heart, Trash, DotsThree, Paperclip, Check
} from '@phosphor-icons/react';

function PostActivityForm({ postActionType, setPostActionType, onSkip, onCreate, mappedEmployees }) {
  const [pf, setPf] = useState({ follow_up_type: '', task_type: '', due_date: '', due_time: '', priority: 'Medium', description: '', notes: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  if (!postActionType) {
    return (
      <div className="space-y-3 py-4">
        <p className="text-sm text-zinc-600">Would you like to create a follow-up or task from this activity?</p>
        <div className="flex gap-3">
          <Button className="flex-1 bg-[#002FA7] text-white" onClick={() => setPostActionType('followup')}>Create Follow-up</Button>
          <Button className="flex-1" variant="outline" onClick={() => setPostActionType('task')}>Create Task</Button>
        </div>
        <Button variant="ghost" className="w-full text-zinc-500 text-sm" onClick={onSkip}>Skip</Button>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!pf.due_date) { toast.error('Please select a due date'); return; }
    setSaving(true);
    await onCreate(postActionType, pf);
    setSaving(false);
  };

  return (
    <div className="space-y-3 py-2">
      {postActionType === 'followup' && (
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Follow-up Type <span className="text-red-500">*</span></Label>
          <SearchableSelect value={pf.follow_up_type} onValueChange={(v) => setPf(p => ({ ...p, follow_up_type: v }))} options={FOLLOW_UP_TYPES.map(t => ({ value: t, label: t }))} placeholder="Select type" />
        </div>
      )}
      {postActionType === 'task' && (
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Task Type <span className="text-red-500">*</span></Label>
          <SearchableSelect value={pf.task_type} onValueChange={(v) => setPf(p => ({ ...p, task_type: v }))} options={TASK_TYPES.map(t => ({ value: t, label: t }))} placeholder="Select type" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Due Date <span className="text-red-500">*</span></Label>
          <DatePicker value={pf.due_date} onChange={(v) => setPf(p => ({ ...p, due_date: v }))} placeholder="Select date" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Due Time</Label>
          <TimePicker value={pf.due_time} onChange={(v) => setPf(p => ({ ...p, due_time: v }))} slots={FOLLOWUP_TIME_SLOTS} placeholder="Select time" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Priority</Label>
          <SearchableSelect value={pf.priority} onValueChange={(v) => setPf(p => ({ ...p, priority: v }))} options={PRIORITIES.map(p => ({ value: p, label: p }))} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase font-semibold">Assigned To</Label>
          <SearchableSelect
            value={pf.assigned_to}
            onValueChange={(v) => setPf(p => ({ ...p, assigned_to: v }))}
            options={(mappedEmployees || []).map(u => ({ value: u.id, label: u.name }))}
            placeholder={mappedEmployees?.length ? "Select employee" : "No mapped employees"}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase font-semibold">Description</Label>
        <Textarea value={pf.description} onChange={(e) => setPf(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="rounded-sm" rows={2} />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase font-semibold">Notes</Label>
        <Input value={pf.notes} onChange={(e) => setPf(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes" className="rounded-sm" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setPostActionType(null)}>Back</Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-[#002FA7] text-white">
          {saving ? 'Creating...' : `Create ${postActionType === 'followup' ? 'Follow-up' : 'Task'}`}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState({});
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [products, setProducts] = useState([]);
  const [sips, setSips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [families, setFamilies] = useState([]);
  const [healthInsurances, setHealthInsurances] = useState([]);
  const [termInsurances, setTermInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showSIPModal, setShowSIPModal] = useState(false);
  const [editingSIP, setEditingSIP] = useState(null);
  const [deleteSIPId, setDeleteSIPId] = useState(null);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingFu, setEditingFu] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [postActivityAction, setPostActivityAction] = useState(null); // {activityId, clientId}
  const [postActionType, setPostActionType] = useState(null);

  // Form states
  const [activityForm, setActivityForm] = useState({ activity_type: '', date: '', time: '', outcome_notes: '', call_status: '', call_duration: '', meeting_status: '', meeting_duration: '', assigned_to: '' });
  const [followUpForm, setFollowUpForm] = useState({ follow_up_type: '', due_date: '', due_time: '', priority: 'Medium', description: '', assigned_to: '' });
  const [taskForm, setTaskForm] = useState({ task_type: '', due_date: '', priority: 'Medium', description: '', assigned_to: '' });
  const [noteContent, setNoteContent] = useState('');
  const [docForm, setDocForm] = useState({ document_type: '', file: null });
  const [sipAmount, setSipAmount] = useState('');
  const [healthForm, setHealthForm] = useState({ company_name: '', plan_name: '', premium_amount: '', policy_type: '', due_date: '', policy_number: '', sum_insured: '', notes: '', file: null });
  const [termForm, setTermForm] = useState({ company_name: '', plan_name: '', premium_amount: '', policy_type: '', due_date: '', policy_number: '', sum_assured: '', notes: '', file: null });
  const [mappedEmployees, setMappedEmployees] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, usersRes] = await Promise.all([
          getClient(id),
          getUsers()
        ]);
        setClient(clientRes.data);
        const usersMap = {};
        usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
        setUsers(usersMap);

        // Fetch related data
        const [activitiesRes, followUpsRes, tasksRes, productsRes, sipsRes, docsRes, notesRes, familiesRes, healthRes, termRes] = await Promise.all([
          getActivities(id),
          getFollowUps({ client_id: id }),
          getTasks({ client_id: id }),
          getProducts({ client_id: id }),
          getSIPs({ client_id: id }),
          getDocuments({ client_id: id }),
          getNotes(id),
          getFamilies({ client_id: id, include_details: true }),
          getHealthInsurances({ client_id: id }),
          getTermInsurances({ client_id: id })
        ]);
        setActivities(activitiesRes.data);
        setFollowUps(followUpsRes.data);
        setTasks(tasksRes.data);
        setProducts(productsRes.data);
        setSips(sipsRes.data);
        setDocuments(docsRes.data);
        setNotes(notesRes.data);
        setFamilies(familiesRes.data);
        setHealthInsurances(healthRes.data);
        setTermInsurances(termRes.data);

        // Fetch mapped employees
        try {
          const mappedRes = await getClientMappedEmployees(id);
          setMappedEmployees(mappedRes.data);
        } catch (e) { console.error('Failed to fetch mapped employees:', e); }
      } catch (error) {
        toast.error(formatApiError(error));
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only data loader; state setters are stable

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      const res = await createActivity({ ...activityForm, client_id: id });
      setActivities([res.data, ...activities]);
      setShowActivityModal(false);
      const isAnswered = activityForm.activity_type === 'Call' && activityForm.call_status === 'Answered';
      const isMet = activityForm.activity_type === 'Meeting' && activityForm.meeting_status === 'Met';
      if (isAnswered || isMet) {
        setPostActivityAction({ activityId: res.data.id, clientId: id, type: activityForm.activity_type });
      }
      setActivityForm({ activity_type: '', date: '', time: '', outcome_notes: '', call_status: '', call_duration: '', meeting_status: '', meeting_duration: '', assigned_to: '' });
      toast.success('Activity logged successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    try {
      const res = await createFollowUp({ ...followUpForm, client_id: id });
      setFollowUps([res.data, ...followUps]);
      setShowFollowUpModal(false);
      setFollowUpForm({ follow_up_type: '', due_date: '', due_time: '', priority: 'Medium', description: '', assigned_to: '' });
      toast.success('Follow-up created successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCompleteFollowUp = async (fuId) => {
    try {
      await updateFollowUp(fuId, { status: 'Completed', completed_at: new Date().toISOString() });
      setFollowUps(prev => prev.map(f => f.id === fuId ? { ...f, status: 'Completed' } : f));
      toast.success('Follow-up marked complete');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleSaveEditFollowUp = async (e) => {
    e.preventDefault();
    if (!editingFu) return;
    try {
      const payload = {
        follow_up_type: editingFu.follow_up_type,
        due_date: editingFu.due_date,
        due_time: editingFu.due_time,
        priority: editingFu.priority,
        description: editingFu.description,
        status: editingFu.status,
      };
      const res = await updateFollowUp(editingFu.id, payload);
      setFollowUps(prev => prev.map(f => f.id === editingFu.id ? { ...f, ...res.data } : f));
      setEditingFu(null);
      toast.success('Follow-up updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const res = await createTask({ ...taskForm, client_id: id });
      setTasks([res.data, ...tasks]);
      setShowTaskModal(false);
      setTaskForm({ task_type: '', due_date: '', priority: 'Medium', description: '', assigned_to: '' });
      toast.success('Task created successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handlePostActivityCreate = async (type, postForm) => {
    try {
      if (type === 'followup') {
        const res = await createFollowUp({
          client_id: id,
          follow_up_type: postForm.follow_up_type || 'General Follow-Up',
          due_date: postForm.due_date,
          due_time: postForm.due_time,
          priority: postForm.priority,
          description: postForm.description,
          source_activity_id: postActivityAction?.activityId,
        });
        setFollowUps([res.data, ...followUps]);
        toast.success('Follow-up created');
      } else {
        const res = await createTask({
          client_id: id,
          task_type: postForm.task_type || 'General',
          due_date: postForm.due_date,
          priority: postForm.priority,
          description: postForm.description,
          source_activity_id: postActivityAction?.activityId,
        });
        setTasks([res.data, ...tasks]);
        toast.success('Task created');
      }
      setPostActivityAction(null);
      setPostActionType(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };



  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const res = await createNote({ client_id: id, content: noteContent });
      setNotes([res.data, ...notes]);
      setShowNoteModal(false);
      setNoteContent('');
      toast.success('Note added successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!docForm.file) return;
    try {
      const res = await uploadDocument(id, docForm.document_type, docForm.file);
      setDocuments([res.data, ...documents]);
      setShowDocModal(false);
      setDocForm({ document_type: '', file: null });
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateSIP = async (e) => {
    e.preventDefault();
    try {
      if (editingSIP) {
        await updateSIP(editingSIP.id, {
          amount: parseFloat(sipAmount),
          status: editingSIP.status
        });
        setSips(sips.map(s => s.id === editingSIP.id ? { ...s, amount: parseFloat(sipAmount) } : s));
        toast.success('SIP updated successfully');
      } else {
        const res = await createSIP({ 
          client_id: id, 
          amount: parseFloat(sipAmount),
          fund_name: 'SIP',
          start_date: new Date().toISOString().split('T')[0],
          frequency: 'Monthly'
        });
        setSips([res.data, ...sips]);
        toast.success('SIP added successfully');
      }
      setShowSIPModal(false);
      setSipAmount('');
      setEditingSIP(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteSIP = async () => {
    if (!deleteSIPId) return;
    try {
      await deleteSIP(deleteSIPId);
      setSips(sips.filter(s => s.id !== deleteSIPId));
      toast.success('SIP deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteSIPId(null);
    }
  };

  const openEditSIP = (sip) => {
    setEditingSIP(sip);
    setSipAmount(String(sip.amount));
    setShowSIPModal(true);
  };

  const handleCreateHealthInsurance = async (e) => {
    e.preventDefault();
    try {
      const { file, ...formFields } = healthForm;
      const res = await createHealthInsurance({
        client_id: id,
        company_name: formFields.company_name,
        plan_name: formFields.plan_name,
        premium_amount: parseFloat(formFields.premium_amount),
        policy_type: formFields.policy_type,
        due_date: formFields.due_date,
        policy_number: formFields.policy_number || undefined,
        sum_insured: formFields.sum_insured ? parseFloat(formFields.sum_insured) : undefined,
        notes: formFields.notes || undefined
      });
      let created = res.data;
      // Upload policy document if provided
      if (file) {
        const uploadRes = await uploadHealthInsuranceDoc(created.id, file);
        created = { ...created, policy_document_url: uploadRes.data.url, policy_document_name: uploadRes.data.filename };
      }
      setHealthInsurances([created, ...healthInsurances]);
      setShowHealthModal(false);
      setHealthForm({ company_name: '', plan_name: '', premium_amount: '', policy_type: '', due_date: '', policy_number: '', sum_insured: '', notes: '', file: null });
      toast.success('Health insurance added successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteHealthInsurance = async (itemId) => {
    try {
      await deleteHealthInsurance(itemId);
      setHealthInsurances(healthInsurances.filter(h => h.id !== itemId));
      toast.success('Health insurance deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleCreateTermInsurance = async (e) => {
    e.preventDefault();
    try {
      const { file, ...formFields } = termForm;
      const res = await createTermInsurance({
        client_id: id,
        company_name: formFields.company_name,
        plan_name: formFields.plan_name,
        premium_amount: parseFloat(formFields.premium_amount),
        policy_type: formFields.policy_type,
        due_date: formFields.due_date,
        policy_number: formFields.policy_number || undefined,
        sum_assured: formFields.sum_assured ? parseFloat(formFields.sum_assured) : undefined,
        notes: formFields.notes || undefined
      });
      let created = res.data;
      // Upload policy document if provided
      if (file) {
        const uploadRes = await uploadTermInsuranceDoc(created.id, file);
        created = { ...created, policy_document_url: uploadRes.data.url, policy_document_name: uploadRes.data.filename };
      }
      setTermInsurances([created, ...termInsurances]);
      setShowTermModal(false);
      setTermForm({ company_name: '', plan_name: '', premium_amount: '', policy_type: '', due_date: '', policy_number: '', sum_assured: '', notes: '', file: null });
      toast.success('Term insurance added successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteTermInsurance = async (itemId) => {
    try {
      await deleteTermInsurance(itemId);
      setTermInsurances(termInsurances.filter(t => t.id !== itemId));
      toast.success('Term insurance deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, {
        status: editingTask.status,
        status_comment: editingTask.status_comment || undefined,
        rejection_reason: editingTask.rejection_reason || undefined,
        mandate_status: editingTask.mandate_status || undefined,
        priority: editingTask.priority,
        due_date: editingTask.due_date,
        folio_number: editingTask.folio_number || undefined,
        amc_name: editingTask.amc_name || undefined
      });
      setTasks(tasks.map(t => t.id === editingTask.id ? { ...t, ...editingTask } : t));
      setEditingTask(null);
      toast.success('Task updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  const isClientMinor = client?.client_type === 'Minor' || isMinor(client?.date_of_birth);

  return (
    <div className="p-6 space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                {client.full_name}
              </h1>
              <Badge className={`${getStatusBadgeClass(client.client_status)} text-xs`}>
                {client.client_status}
              </Badge>
              {isClientMinor && (
                <Badge className="bg-purple-100 text-purple-700 text-xs">Minor</Badge>
              )}
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              {client.tax_status} {client.kyc_status && `• ${client.kyc_status}`}
              {sips.filter(s => !s.status || !['Cancelled', 'Stopped'].includes(s.status)).length > 0 && (
                <span className="ml-2 text-[#002FA7] font-medium">
                  • SIP: {formatCurrency(sips.filter(s => !s.status || !['Cancelled', 'Stopped'].includes(s.status)).reduce((sum, s) => sum + (s.amount || 0), 0))}/mo
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/clients/${id}/edit`)} className="rounded-sm">
            <Pencil size={16} className="mr-2" /> Edit
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        {client.client_status !== 'Deceased' && (
          <>
            <a href={`tel:${client.phone}`}>
              <Button variant="outline" size="sm" className="rounded-sm">
                <Phone size={16} className="mr-2" /> Call
              </Button>
            </a>
            <a href={`https://wa.me/91${client.secondary_phone || client.phone}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="rounded-sm">
                <ChatCircle size={16} className="mr-2" /> WhatsApp
              </Button>
            </a>
          </>
        )}
        <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-sm">
              <Plus size={16} className="mr-2" /> Activity
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateActivity} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase">Activity Type *</Label>
                <SearchableSelect
                  value={activityForm.activity_type}
                  onValueChange={(v) => setActivityForm(p => ({ ...p, activity_type: v, call_status: '', call_duration: '', meeting_status: '', meeting_duration: '' }))}
                  options={ACTIVITY_TYPES}
                  placeholder="Select type"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Date *</Label>
                  <DatePicker value={activityForm.date} onChange={(v) => setActivityForm(p => ({ ...p, date: v }))} maxDate={new Date().toISOString().split('T')[0]} placeholder="Select date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Time *</Label>
                  <TimePicker
                    value={activityForm.time || ''}
                    onChange={(v) => setActivityForm(p => ({ ...p, time: v }))}
                    slots={activityForm.activity_type === 'Call' || activityForm.activity_type === 'Meeting' ? BUSINESS_TIME_SLOTS : TIME_SLOTS}
                    placeholder="Select time"
                  />
                </div>
              </div>
              {/* Call Status */}
              {activityForm.activity_type === 'Call' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase">Call Status *</Label>
                    <SearchableSelect
                      value={activityForm.call_status}
                      onValueChange={(v) => setActivityForm(p => ({ ...p, call_status: v, call_duration: v !== 'Answered' ? '' : p.call_duration }))}
                      options={CALL_STATUSES}
                      placeholder="Select status"
                    />
                  </div>
                  {activityForm.call_status === 'Answered' && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Call Duration</Label>
                      <SearchableSelect
                        value={activityForm.call_duration}
                        onValueChange={(v) => setActivityForm(p => ({ ...p, call_duration: v }))}
                        options={CALL_DURATIONS}
                        placeholder="Select duration"
                      />
                    </div>
                  )}
                </div>
              )}
              {/* Meeting Status */}
              {activityForm.activity_type === 'Meeting' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase">Meeting Status *</Label>
                    <SearchableSelect
                      value={activityForm.meeting_status}
                      onValueChange={(v) => setActivityForm(p => ({ ...p, meeting_status: v, meeting_duration: v !== 'Met' ? '' : p.meeting_duration }))}
                      options={MEETING_STATUSES}
                      placeholder="Select status"
                    />
                  </div>
                  {activityForm.meeting_status === 'Met' && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Meeting Duration</Label>
                      <SearchableSelect
                        value={activityForm.meeting_duration}
                        onValueChange={(v) => setActivityForm(p => ({ ...p, meeting_duration: v }))}
                        options={MEETING_DURATIONS}
                        placeholder="Select duration"
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase">Logged By</Label>
                <SearchableSelect
                  value={activityForm.assigned_to}
                  onValueChange={(v) => setActivityForm(p => ({ ...p, assigned_to: v }))}
                  options={mappedEmployees.map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select employee"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Outcome / Notes *</Label>
                <Textarea value={activityForm.outcome_notes} onChange={(e) => setActivityForm(p => ({ ...p, outcome_notes: e.target.value }))} required className="rounded-sm" rows={3} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#002FA7] text-white">Log Activity</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showFollowUpModal} onOpenChange={setShowFollowUpModal}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-sm">
              <CalendarCheck size={16} className="mr-2" /> Follow-up
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Follow-up</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateFollowUp} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase">Type *</Label>
                <SearchableSelect
                  value={followUpForm.follow_up_type}
                  onValueChange={(v) => setFollowUpForm(p => ({ ...p, follow_up_type: v }))}
                  options={FOLLOW_UP_TYPES}
                  placeholder="Select type"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Due Date *</Label>
                  <DatePicker value={followUpForm.due_date} onChange={(v) => setFollowUpForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Due Time *</Label>
                  <TimePicker
                    value={followUpForm.due_time || ''}
                    onChange={(v) => setFollowUpForm(p => ({ ...p, due_time: v }))}
                    slots={FOLLOWUP_TIME_SLOTS}
                    placeholder="Select time"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Priority *</Label>
                <SearchableSelect
                  value={followUpForm.priority}
                  onValueChange={(v) => setFollowUpForm(p => ({ ...p, priority: v }))}
                  options={PRIORITIES}
                  placeholder="Select priority"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Assigned To</Label>
                <SearchableSelect
                  value={followUpForm.assigned_to}
                  onValueChange={(v) => setFollowUpForm(p => ({ ...p, assigned_to: v }))}
                  options={mappedEmployees.map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select employee"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Description</Label>
                <Textarea value={followUpForm.description} onChange={(e) => setFollowUpForm(p => ({ ...p, description: e.target.value }))} className="rounded-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowFollowUpModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#002FA7] text-white">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-sm">
              <ClipboardText size={16} className="mr-2" /> Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Type *</Label>
                  <SearchableSelect
                    value={taskForm.task_type}
                    onValueChange={(v) => setTaskForm(p => ({ ...p, task_type: v }))}
                    groups={Object.entries(TASK_CATEGORY_MAP).map(([cat, types]) => ({ label: cat, options: types }))}
                    placeholder="Select type"
                    data-testid="task-type-select"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Due Date *</Label>
                  <DatePicker value={taskForm.due_date} onChange={(v) => setTaskForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Priority *</Label>
                <SearchableSelect
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm(p => ({ ...p, priority: v }))}
                  options={PRIORITIES}
                  placeholder="Select priority"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Assigned To</Label>
                <SearchableSelect
                  value={taskForm.assigned_to}
                  onValueChange={(v) => setTaskForm(p => ({ ...p, assigned_to: v }))}
                  options={mappedEmployees.map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select employee"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Description</Label>
                <Textarea value={taskForm.description} onChange={(e) => setTaskForm(p => ({ ...p, description: e.target.value }))} className="rounded-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#002FA7] text-white">Create</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Content with Family Sidebar */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Tabs */}
        <div className={families.length > 0 ? 'flex-1 min-w-0' : 'w-full'}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-sm w-full overflow-x-auto justify-start flex-nowrap no-scrollbar">
          <TabsTrigger value="overview" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Overview</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Activity</TabsTrigger>
          <TabsTrigger value="follow-ups" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Follow-ups</TabsTrigger>
          <TabsTrigger value="tasks" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Tasks</TabsTrigger>
          <TabsTrigger value="products" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Products</TabsTrigger>
          <TabsTrigger value="insurance" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0" data-testid="insurance-tab">Insurance</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Documents</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-sm text-xs uppercase tracking-wider flex-shrink-0">Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          {/* Portfolio Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="border-zinc-200 rounded-sm" data-testid="overview-entity-type">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Entity Type</p>
                <p className="text-lg font-bold">{client.client_type || 'Individual'}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 rounded-sm" data-testid="overview-aum-range">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">AUM Range</p>
                <p className="text-lg font-bold">{client.aum_range || '-'}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 rounded-sm" data-testid="overview-sip-amount">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Total SIP</p>
                <p className="text-lg font-bold">{formatCurrency(sips.filter(s => !s.status || !['Cancelled', 'Stopped'].includes(s.status)).reduce((sum, s) => sum + (s.amount || 0), 0))}/mo</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 rounded-sm" data-testid="overview-health-insurance">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Health Policies</p>
                <p className="text-lg font-bold">{healthInsurances.filter(h => h.status === 'Active').length}</p>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 rounded-sm" data-testid="overview-term-insurance">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Term Insurance</p>
                <p className="text-lg font-bold">{termInsurances.filter(t => t.status === 'Active').length}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tracking Dates Card - spans full width */}
            <Card className="border-zinc-200 rounded-sm lg:col-span-3 border-[#002FA7]/20 bg-blue-50/20">
              <CardHeader className="border-b border-[#002FA7]/10">
                <CardTitle className="text-base text-[#002FA7]">Activity Tracking</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Client Type</p>
                    <Badge className="bg-zinc-100 text-zinc-700 text-xs">{client.client_type || 'Individual'}</Badge>
                  </div>
                  <div data-testid="last-portfolio-reviewed">
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Last Portfolio Reviewed</p>
                    <p className="text-sm font-medium">{formatDate(client.last_portfolio_reviewed_on) || '-'}</p>
                  </div>
                  <div data-testid="last-meeting-date">
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Last Meeting</p>
                    <p className="text-sm font-medium">{client.last_meeting_date || '-'}</p>
                  </div>
                  <div data-testid="last-call-date">
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Last Call</p>
                    <p className="text-sm font-medium">{client.last_call_date || '-'}</p>
                  </div>
                  {/* Show as Individual toggle - only for family members */}
                  {families.some(f => f.members?.some(m => m.client_id === id) || f.head_client_id === id) && (
                    <div data-testid="individual-toggle">
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Show as Individual</p>
                      <button
                        onClick={async () => {
                          try {
                            const res = await toggleClientIndividual(id);
                            setClient(prev => ({ ...prev, show_as_individual: res.data.show_as_individual }));
                            toast.success(res.data.show_as_individual ? 'Client will now appear individually' : 'Client removed from individual list');
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${client.show_as_individual ? 'bg-[#002FA7]' : 'bg-zinc-300'}`}
                        data-testid="individual-toggle-btn"
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${client.show_as_individual ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      {client.show_as_individual && <Badge className="ml-2 bg-blue-100 text-blue-700 text-[10px]">Family Member</Badge>}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Entity Details (for non-Individual types) */}
            {client.client_type && client.client_type !== 'Individual' && client.client_type !== 'NRI' && (
              <Card className="border-zinc-200 rounded-sm lg:col-span-3 border-amber-200 bg-amber-50/20">
                <CardHeader className="border-b border-amber-100">
                  <CardTitle className="text-base text-amber-700">{client.client_type} Details</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {client.entity_name && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Entity Name</p>
                        <p className="text-sm">{client.entity_name}</p>
                      </div>
                    )}
                    {client.incorporation_date && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Date of Incorporation</p>
                        <p className="text-sm">{formatDate(client.incorporation_date)}</p>
                      </div>
                    )}
                    {client.karta_name && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Karta Name</p>
                        <p className="text-sm">{client.karta_name}</p>
                      </div>
                    )}
                    {client.authorized_signatory && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Authorized Signatory</p>
                        <p className="text-sm">{client.authorized_signatory}</p>
                      </div>
                    )}
                    {client.registration_number && (
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Registration Number</p>
                        <p className="text-sm">{client.registration_number}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-zinc-200 rounded-sm lg:col-span-2">
              <CardHeader className="border-b border-zinc-100"><CardTitle className="text-base">Client Information</CardTitle></CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Calling Number</p>
                    <p className="flex items-center gap-2"><Phone size={16} className="text-zinc-400" /> {formatPhone(client.phone)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">WhatsApp Number</p>
                    <p className="flex items-center gap-2"><Phone size={16} className="text-zinc-400" /> {formatPhone(client.secondary_phone || client.phone)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Email</p>
                    <p className="flex items-center gap-2"><Envelope size={16} className="text-zinc-400" /> {client.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">PAN</p>
                    <p className="flex items-center gap-2"><IdentificationCard size={16} className="text-zinc-400" /> {maskPAN(client.pan_number)}</p>
                  </div>
                  {client.date_of_birth && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Date of Birth</p>
                      <p className="flex items-center gap-2"><CalendarBlank size={16} className="text-zinc-400" /> {formatDate(client.date_of_birth)}</p>
                    </div>
                  )}
                  {client.gender && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Gender</p>
                      <p>{client.gender}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Profession</p>
                    <p>{client.profession}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Address</p>
                    <p className="flex items-center gap-2"><MapPin size={16} className="text-zinc-400" /> {[client.address, client.city, client.state, client.pin_code].filter(Boolean).join(', ') || '—'}</p>
                  </div>
                  {client.country_code && client.country_code !== '+91' && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Country Code</p>
                      <p>{client.country_code}</p>
                    </div>
                  )}
                  {client.foreign_address && (
                    <div className="col-span-2">
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Foreign Address</p>
                      <p className="flex items-center gap-2"><MapPin size={16} className="text-zinc-400" /> {[client.foreign_address, client.foreign_city, client.foreign_country, client.foreign_zip].filter(Boolean).join(', ')}</p>
                    </div>
                  )}
                  {client.aum_range && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">AUM Range</p>
                      <p>{client.aum_range}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="border-b border-zinc-100"><CardTitle className="text-base">KYC Details</CardTitle></CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Tax Status</p>
                    <p>{client.tax_status}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">KYC Type</p>
                    <p>{client.kyc_acquisition_type}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">KYC Status</p>
                    <Badge className={`${getStatusBadgeClass(client.kyc_status === 'Validated' ? 'Completed' : client.kyc_status === 'Rejected' ? 'Rejection' : 'Pending')} text-xs`}>
                      {client.kyc_status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200 rounded-sm">
                <CardHeader className="border-b border-zinc-100"><CardTitle className="text-base">Assigned Team</CardTitle></CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Account Manager</p>
                      <p>{client.account_managers?.map(id => users[id]).filter(Boolean).join(', ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Sr. Account Manager</p>
                      <p>{users[client.senior_account_manager] || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Operations Manager</p>
                      <p>{client.operations_managers?.map(id => users[id]).filter(Boolean).join(', ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-1">Sr. Operations Manager</p>
                      <p>{users[client.senior_operations_manager] || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {activities.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No activities logged yet</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {activities.map(act => (
                    <div key={act.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-zinc-100 rounded-sm">
                          {act.activity_type === 'Call' && <Phone size={16} />}
                          {act.activity_type === 'Meeting' && <UsersThree size={16} />}
                          {act.activity_type === 'WhatsApp' && <ChatCircle size={16} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{act.activity_type}</p>
                              {act.call_status && (
                                <Badge className={`text-[10px] ${act.call_status === 'Answered' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                                  {act.call_status}
                                </Badge>
                              )}
                              {act.meeting_status && (
                                <Badge className={`text-[10px] ${act.meeting_status === 'Met' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                                  {act.meeting_status}
                                </Badge>
                              )}
                              {act.call_duration && <span className="text-[10px] text-zinc-500">({act.call_duration})</span>}
                              {act.meeting_duration && <span className="text-[10px] text-zinc-500">({act.meeting_duration})</span>}
                            </div>
                            <p className="text-xs text-zinc-500">{formatDate(act.date)} {act.time}</p>
                          </div>
                          <p className="text-sm text-zinc-600 mt-1">{act.outcome_notes || act.description}</p>
                          <p className="text-xs text-zinc-400 mt-2">By {users[act.logged_by] || 'Unknown'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="follow-ups" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100"><CardTitle className="text-base">Follow-ups</CardTitle></CardHeader>
            <CardContent className="p-0">
              {followUps.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No follow-ups scheduled</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {followUps.map(fu => (
                    <div key={fu.id} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`client-fu-row-${fu.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-sm">{fu.follow_up_type}</p>
                          <Badge className={`${getPriorityBadgeClass(fu.priority)} text-xs`}>{fu.priority}</Badge>
                          <Badge className={`${getStatusBadgeClass(fu.status)} text-xs`}>{fu.status}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">{formatDate(fu.due_date)} {fu.due_time}</p>
                        {fu.description && <p className="text-sm text-zinc-600 mt-1 break-words">{fu.description}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {fu.status !== 'Completed' && (
                          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => handleCompleteFollowUp(fu.id)} data-testid={`client-fu-complete-${fu.id}`}>
                            <Check size={14} className="mr-1" /> Complete
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditingFu({ ...fu })} data-testid={`client-fu-edit-${fu.id}`}>
                          <Pencil size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Follow-up Dialog */}
          <Dialog open={!!editingFu} onOpenChange={(open) => !open && setEditingFu(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Follow-up</DialogTitle>
                <DialogDescription className="sr-only">Update follow-up details or mark as completed.</DialogDescription>
              </DialogHeader>
              {editingFu && (
                <form onSubmit={handleSaveEditFollowUp} className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Type</Label>
                      <SearchableSelect
                        value={editingFu.follow_up_type}
                        onValueChange={(v) => setEditingFu(p => ({ ...p, follow_up_type: v }))}
                        options={FOLLOW_UP_TYPES.map(t => ({ value: t, label: t }))}
                        placeholder="Select type"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Priority</Label>
                      <SearchableSelect
                        value={editingFu.priority}
                        onValueChange={(v) => setEditingFu(p => ({ ...p, priority: v }))}
                        options={PRIORITIES.map(p => ({ value: p, label: p }))}
                        placeholder="Select priority"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Due Date</Label>
                      <DatePicker
                        value={editingFu.due_date}
                        onChange={(v) => setEditingFu(p => ({ ...p, due_date: v }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Due Time</Label>
                      <TimePicker
                        value={editingFu.due_time || ''}
                        onChange={(v) => setEditingFu(p => ({ ...p, due_time: v }))}
                        slots={FOLLOWUP_TIME_SLOTS}
                        placeholder="Select time"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-xs uppercase">Status</Label>
                      <SearchableSelect
                        value={editingFu.status}
                        onValueChange={(v) => setEditingFu(p => ({ ...p, status: v }))}
                        options={[{value:'Pending',label:'Pending'},{value:'In Progress',label:'In Progress'},{value:'Completed',label:'Completed'},{value:'Cancelled',label:'Cancelled'}]}
                        placeholder="Select status"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-xs uppercase">Description</Label>
                      <textarea
                        className="w-full rounded-sm border border-zinc-200 p-2 text-sm"
                        rows={3}
                        value={editingFu.description || ''}
                        onChange={(e) => setEditingFu(p => ({ ...p, description: e.target.value }))}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setEditingFu(null)}>Cancel</Button>
                    <Button type="submit" className="bg-[#002FA7] text-white" data-testid="client-fu-save">Save</Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6 space-y-4">
          {/* Task Edit Modal */}
          <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Update Task — {editingTask?.task_type}</DialogTitle></DialogHeader>
              {editingTask && (
                <form onSubmit={handleUpdateTask} className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Status *</Label>
                      <SearchableSelect
                        value={editingTask.status}
                        onValueChange={(v) => setEditingTask(p => ({ ...p, status: v }))}
                        options={TASK_STATUSES}
                        placeholder="Select status"
                        data-testid="edit-task-status"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Priority</Label>
                      <SearchableSelect
                        value={editingTask.priority}
                        onValueChange={(v) => setEditingTask(p => ({ ...p, priority: v }))}
                        options={PRIORITIES}
                        placeholder="Select priority"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Due Date</Label>
                      <DatePicker value={editingTask.due_date || ''} onChange={(v) => setEditingTask(p => ({ ...p, due_date: v }))} placeholder="Select due date" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">AMC Name</Label>
                      <SearchableSelect
                        value={editingTask.amc_name || ''}
                        onValueChange={(v) => setEditingTask(p => ({ ...p, amc_name: v }))}
                        options={AMC_NAMES}
                        placeholder="Select AMC"
                        searchPlaceholder="Search AMC..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Folio Number</Label>
                      <Input value={editingTask.folio_number || ''} onChange={(e) => setEditingTask(p => ({ ...p, folio_number: e.target.value }))} className="rounded-sm" />
                    </div>
                    {MANDATE_TASK_TYPES.includes(editingTask.task_type) && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Mandate Status</Label>
                        <SearchableSelect
                          value={editingTask.mandate_status || ''}
                          onValueChange={(v) => setEditingTask(p => ({ ...p, mandate_status: v }))}
                          options={MANDATE_STATUSES}
                          placeholder="Select"
                        />
                      </div>
                    )}
                    {editingTask.status === 'Rejection' && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Rejection Reason</Label>
                        <SearchableSelect
                          value={editingTask.rejection_reason || ''}
                          onValueChange={(v) => setEditingTask(p => ({ ...p, rejection_reason: v }))}
                          options={REJECTION_REASONS}
                          placeholder="Select"
                        />
                      </div>
                    )}
                    <div className="col-span-2 space-y-2">
                      <Label className="text-xs uppercase">Comment</Label>
                      <Textarea value={editingTask.status_comment || ''} onChange={(e) => setEditingTask(p => ({ ...p, status_comment: e.target.value }))} className="rounded-sm" rows={2} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
                    <Button type="submit" className="bg-[#002FA7] text-white" data-testid="save-task-update-btn">Save Changes</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Task List */}
          {tasks.length === 0 ? (
            <Card className="border-zinc-200 rounded-sm">
              <CardContent className="p-6 text-center text-zinc-500">No tasks assigned</CardContent>
            </Card>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="client-tasks-list">
              <table className="w-full">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Type</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Category</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Status</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Priority</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Due Date</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5">Details</th>
                    <th className="text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-500 px-4 py-2.5 w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {tasks.map(task => {
                    const cat = getTaskCategory(task.task_type);
                    return (
                      <tr key={task.id} className="hover:bg-zinc-50 transition-colors" data-testid={`client-task-${task.id}`}>
                        <td className="px-4 py-3">
                          <button
                            className="font-medium text-sm text-[#002FA7] hover:underline cursor-pointer bg-transparent border-0 p-0 text-left"
                            onClick={() => setViewingTask(task)}
                            data-testid={`view-client-task-${task.id}`}
                          >
                            {task.task_type}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[10px] ${cat === 'NFT' ? 'bg-amber-100 text-amber-700' : cat === 'FT' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{cat}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${getStatusBadgeClass(task.status)} text-xs`}>{task.status}</Badge>
                          {MANDATE_TASK_TYPES.includes(task.task_type) && task.mandate_status && (
                            <Badge className="bg-violet-100 text-violet-700 text-[10px] ml-1">M: {task.mandate_status}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${getPriorityBadgeClass(task.priority)} text-xs`}>{task.priority}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-600">{formatDate(task.due_date)}</td>
                        <td className="px-4 py-3 text-xs text-zinc-500">
                          {task.folio_number && <span>Folio: {task.folio_number} </span>}
                          {task.amc_name && <span>AMC: {task.amc_name} </span>}
                          {task.sip_id && <span className="text-green-600">SIP Linked</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setEditingTask({ ...task })}
                            data-testid={`edit-task-${task.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Task Detail View Dialog */}
        <Dialog open={!!viewingTask} onOpenChange={() => setViewingTask(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">Task Details</DialogTitle>
            </DialogHeader>
            {viewingTask && (
              <div className="space-y-4 py-2" data-testid="client-task-detail-popup">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Task Type</p>
                    <p className="font-medium">{viewingTask.task_type}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Category</p>
                    <Badge className={`text-xs ${getTaskCategory(viewingTask.task_type) === 'NFT' ? 'bg-amber-100 text-amber-700' : getTaskCategory(viewingTask.task_type) === 'FT' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {getTaskCategory(viewingTask.task_type)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Status</p>
                    <Badge className={`${getStatusBadgeClass(viewingTask.status)} text-xs`}>{viewingTask.status}</Badge>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Priority</p>
                    <Badge className={`${getPriorityBadgeClass(viewingTask.priority)} text-xs`}>{viewingTask.priority}</Badge>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Due Date</p>
                    <p className="text-sm">{formatDate(viewingTask.due_date)} {viewingTask.due_time || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Assigned To</p>
                    <p className="text-sm">{viewingTask.assigned_to_name || viewingTask.assigned_to || 'Unassigned'}</p>
                  </div>
                  {viewingTask.amc_name && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">AMC Name</p>
                      <p className="text-sm">{viewingTask.amc_name}</p>
                    </div>
                  )}
                  {viewingTask.folio_number && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Folio Number</p>
                      <p className="text-sm">{viewingTask.folio_number}</p>
                    </div>
                  )}
                  {viewingTask.folio_type && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Folio Type</p>
                      <p className="text-sm capitalize">{viewingTask.folio_type}</p>
                    </div>
                  )}
                  {viewingTask.amount && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Amount</p>
                      <p className="text-sm font-medium text-green-700">{formatCurrency(viewingTask.amount)}</p>
                    </div>
                  )}
                  {viewingTask.purchase_sub_type && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Purchase Type</p>
                      <p className="text-sm">{viewingTask.purchase_sub_type}</p>
                    </div>
                  )}
                  {viewingTask.mandate_status && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Mandate Status</p>
                      <p className="text-sm">{viewingTask.mandate_status}</p>
                    </div>
                  )}
                  {viewingTask.rejection_reason && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400">Rejection Reason</p>
                      <p className="text-sm text-red-600">{viewingTask.rejection_reason}</p>
                    </div>
                  )}
                </div>

                {viewingTask.description && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-1">Description</p>
                    <p className="text-sm text-zinc-700 bg-zinc-50 p-3 rounded-sm">{viewingTask.description}</p>
                  </div>
                )}

                {viewingTask.attachments?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Attachments</p>
                    <div className="space-y-2">
                      {viewingTask.attachments.map((att, idx) => (
                        <a
                          key={att.url || att.key || `att-${idx}`}
                          href={att.url || att}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-blue-50 rounded-sm border border-blue-100 hover:bg-blue-100 transition-colors"
                        >
                          <Paperclip size={16} className="text-[#002FA7]" />
                          <span className="text-sm text-[#002FA7] flex-1 truncate">{att.name || att.filename || `Attachment ${idx + 1}`}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {viewingTask.attachment_url && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Attachment</p>
                    <a
                      href={viewingTask.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-blue-50 rounded-sm border border-blue-100 hover:bg-blue-100 transition-colors"
                    >
                      <Paperclip size={16} className="text-[#002FA7]" />
                      <span className="text-sm text-[#002FA7] flex-1">View Attachment</span>
                    </a>
                  </div>
                )}

                {viewingTask.status_history?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-400 mb-2">Status History</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {viewingTask.status_history.map((h, idx) => (
                        <div key={h.changed_at || `hist-${idx}`} className="p-2 bg-zinc-50 rounded-sm border border-zinc-100 text-sm">
                          <div className="flex items-center justify-between">
                            <Badge className={`${getStatusBadgeClass(h.status)} text-xs`}>{h.status}</Badge>
                            <span className="text-xs text-zinc-400">{formatDate(h.changed_at)}</span>
                          </div>
                          {h.comment && <p className="mt-1 text-zinc-600 text-xs">{h.comment}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2 border-t border-zinc-100">
                  <Button variant="outline" onClick={() => setViewingTask(null)} className="rounded-sm">Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Products Tab */}
        <TabsContent value="products" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Products & SIPs</CardTitle>
              <div className="flex gap-2">
                <Dialog open={showSIPModal} onOpenChange={(open) => { setShowSIPModal(open); if (!open) { setEditingSIP(null); setSipAmount(''); } }}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="rounded-sm" data-testid="add-sip-button">
                      <Plus size={16} className="mr-2" /> Add SIP
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{editingSIP ? 'Edit' : 'Add'} SIP</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateSIP} className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">SIP Amount (Monthly) *</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={sipAmount}
                          onChange={(e) => setSipAmount(e.target.value)}
                          required
                          placeholder="e.g., 5000"
                          className="rounded-sm"
                          data-testid="sip-amount-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => { setShowSIPModal(false); setEditingSIP(null); setSipAmount(''); }}>Cancel</Button>
                        <Button type="submit" className="bg-[#002FA7] text-white" data-testid="submit-sip-button">{editingSIP ? 'Update' : 'Add'} SIP</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {products.length === 0 && sips.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No products tagged</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {products.map(prod => (
                    <div key={prod.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          <Tag size={16} className="text-zinc-400" />
                          {prod.product_type}
                        </p>
                        {prod.details?.insurance_company && (
                          <p className="text-xs text-zinc-500 mt-1">{prod.details.insurance_company} • Policy: {prod.details.policy_number}</p>
                        )}
                      </div>
                      <p className="text-sm font-semibold tabular-nums">
                        {prod.details?.sum_insured && formatCurrency(prod.details.sum_insured)}
                        {prod.details?.amount && formatCurrency(prod.details.amount)}
                      </p>
                    </div>
                  ))}
                  {sips.map(sip => (
                    <div key={sip.id} className="p-4 flex items-center justify-between" data-testid={`sip-row-${sip.id}`}>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          <Clock size={16} className="text-zinc-400" />
                          SIP ({sip.frequency})
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">Started: {formatDate(sip.start_date)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-semibold tabular-nums">{formatCurrency(sip.amount)}/month</p>
                          <Badge className={`${getStatusBadgeClass(sip.status)} text-xs mt-1`}>{sip.status}</Badge>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`sip-actions-${sip.id}`}>
                              <DotsThree size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditSIP(sip)} data-testid={`edit-sip-${sip.id}`}>
                              <Pencil size={16} className="mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeleteSIPId(sip.id)} className="text-red-600" data-testid={`delete-sip-${sip.id}`}>
                              <Trash size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Tab */}
        <TabsContent value="insurance" className="mt-6 space-y-6">
          {/* Health Insurance */}
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Heart size={18} className="text-red-500" /> Health Insurance
              </CardTitle>
              <Dialog open={showHealthModal} onOpenChange={setShowHealthModal}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-sm" data-testid="add-health-insurance-btn">
                    <Plus size={16} className="mr-2" /> Add Policy
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Add Health Insurance</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateHealthInsurance} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Company Name *</Label>
                        <SearchableSelect
                          value={healthForm.company_name}
                          onValueChange={(v) => setHealthForm(p => ({ ...p, company_name: v }))}
                          options={['Star', 'Niva Bupa', 'Care', 'HDFC Ergo', 'ICICI Lombard', 'Max Bupa', 'Bajaj Allianz', 'Other']}
                          placeholder="Select company"
                          data-testid="health-company-select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Plan Name *</Label>
                        <Input value={healthForm.plan_name} onChange={(e) => setHealthForm(p => ({ ...p, plan_name: e.target.value }))} required className="rounded-sm" data-testid="health-plan-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Premium Amount *</Label>
                        <Input type="number" min="1" value={healthForm.premium_amount} onChange={(e) => setHealthForm(p => ({ ...p, premium_amount: e.target.value }))} required className="rounded-sm" data-testid="health-premium-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Type *</Label>
                        <SearchableSelect
                          value={healthForm.policy_type}
                          onValueChange={(v) => setHealthForm(p => ({ ...p, policy_type: v }))}
                          options={['Individual', 'Floater']}
                          placeholder="Select type"
                          data-testid="health-type-select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Due Date *</Label>
                        <DatePicker value={healthForm.due_date} onChange={(v) => setHealthForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" data-testid="health-due-date-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Policy Number</Label>
                        <Input value={healthForm.policy_number} onChange={(e) => setHealthForm(p => ({ ...p, policy_number: e.target.value }))} className="rounded-sm" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Sum Insured</Label>
                        <Input type="number" min="0" value={healthForm.sum_insured} onChange={(e) => setHealthForm(p => ({ ...p, sum_insured: e.target.value }))} className="rounded-sm" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Notes</Label>
                      <Textarea value={healthForm.notes} onChange={(e) => setHealthForm(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Policy Document</Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setHealthForm(p => ({ ...p, file: e.target.files[0] || null }))} className="rounded-sm" data-testid="health-file-input" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowHealthModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white" data-testid="submit-health-insurance-btn">Add Policy</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {healthInsurances.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No health insurance policies added</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {healthInsurances.map(policy => (
                    <div key={policy.id} className="p-4 flex items-center justify-between" data-testid={`health-policy-${policy.id}`}>
                      <div>
                        <p className="font-medium text-sm">{policy.company_name} — {policy.plan_name}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {policy.policy_type} • Premium: {formatCurrency(policy.premium_amount)} • Due: {formatDate(policy.due_date)}
                        </p>
                        {policy.policy_number && <p className="text-xs text-zinc-400 mt-0.5">Policy #{policy.policy_number}</p>}
                        {(policy.policy_document_path || policy.policy_document_url) && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/health-insurances/${policy.id}/document`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline mt-1">
                            <Paperclip size={12} /> {policy.policy_document_name || 'View Document'}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {policy.sum_insured && <span className="text-sm font-semibold tabular-nums">{formatCurrency(policy.sum_insured)}</span>}
                        <Badge className={`${policy.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'} text-xs`}>{policy.status}</Badge>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteHealthInsurance(policy.id)}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Term Insurance */}
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck size={18} className="text-blue-600" /> Term Insurance
              </CardTitle>
              <Dialog open={showTermModal} onOpenChange={setShowTermModal}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-sm" data-testid="add-term-insurance-btn">
                    <Plus size={16} className="mr-2" /> Add Policy
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Add Term Insurance</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateTermInsurance} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Company Name *</Label>
                        <SearchableSelect
                          value={termForm.company_name}
                          onValueChange={(v) => setTermForm(p => ({ ...p, company_name: v }))}
                          options={['HDFC Life', 'ICICI Prudential', 'Max Life', 'Bajaj Allianz', 'Tata AIA', 'LIC', 'SBI Life', 'Other']}
                          placeholder="Select company"
                          data-testid="term-company-select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Plan Name *</Label>
                        <Input value={termForm.plan_name} onChange={(e) => setTermForm(p => ({ ...p, plan_name: e.target.value }))} required className="rounded-sm" data-testid="term-plan-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Premium Amount *</Label>
                        <Input type="number" min="1" value={termForm.premium_amount} onChange={(e) => setTermForm(p => ({ ...p, premium_amount: e.target.value }))} required className="rounded-sm" data-testid="term-premium-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Type *</Label>
                        <SearchableSelect
                          value={termForm.policy_type}
                          onValueChange={(v) => setTermForm(p => ({ ...p, policy_type: v }))}
                          options={['Individual', 'Floater']}
                          placeholder="Select type"
                          data-testid="term-type-select"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Due Date *</Label>
                        <DatePicker value={termForm.due_date} onChange={(v) => setTermForm(p => ({ ...p, due_date: v }))} placeholder="Select due date" data-testid="term-due-date-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Policy Number</Label>
                        <Input value={termForm.policy_number} onChange={(e) => setTermForm(p => ({ ...p, policy_number: e.target.value }))} className="rounded-sm" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Sum Assured</Label>
                        <Input type="number" min="0" value={termForm.sum_assured} onChange={(e) => setTermForm(p => ({ ...p, sum_assured: e.target.value }))} className="rounded-sm" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Notes</Label>
                      <Textarea value={termForm.notes} onChange={(e) => setTermForm(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Policy Document</Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setTermForm(p => ({ ...p, file: e.target.files[0] || null }))} className="rounded-sm" data-testid="term-file-input" />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowTermModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white" data-testid="submit-term-insurance-btn">Add Policy</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {termInsurances.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No term insurance policies added</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {termInsurances.map(policy => (
                    <div key={policy.id} className="p-4 flex items-center justify-between" data-testid={`term-policy-${policy.id}`}>
                      <div>
                        <p className="font-medium text-sm">{policy.company_name} — {policy.plan_name}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          {policy.policy_type} • Premium: {formatCurrency(policy.premium_amount)} • Due: {formatDate(policy.due_date)}
                        </p>
                        {policy.policy_number && <p className="text-xs text-zinc-400 mt-0.5">Policy #{policy.policy_number}</p>}
                        {(policy.policy_document_path || policy.policy_document_url) && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/term-insurances/${policy.id}/document`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline mt-1">
                            <Paperclip size={12} /> {policy.policy_document_name || 'View Document'}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {policy.sum_assured && <span className="text-sm font-semibold tabular-nums">{formatCurrency(policy.sum_assured)}</span>}
                        <Badge className={`${policy.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'} text-xs`}>{policy.status}</Badge>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteTermInsurance(policy.id)}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Documents</CardTitle>
              <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm">
                    <Upload size={16} className="mr-2" /> Upload
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
                  <form onSubmit={handleUploadDocument} className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Document Type *</Label>
                      <SearchableSelect
                        value={docForm.document_type}
                        onValueChange={(v) => setDocForm(p => ({ ...p, document_type: v }))}
                        options={DOCUMENT_TYPES}
                        placeholder="Select type"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">File *</Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocForm(p => ({ ...p, file: e.target.files[0] }))} required className="rounded-sm" />
                      <p className="text-xs text-zinc-500">Accepted: PDF, JPG, PNG (Max 10MB)</p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowDocModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white">Upload</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {documents.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No documents uploaded</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {documents.map(doc => (
                    <div key={doc.id} className="p-4 flex items-center justify-between" data-testid={`doc-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-zinc-100 rounded-sm">
                          <Folder size={16} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{doc.document_type}</p>
                          <p className="text-xs text-zinc-500">{doc.file_name} {doc.size ? `(${(doc.size / 1024).toFixed(0)} KB)` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">{formatDate(doc.created_at)}</span>
                        <a href={getDocumentUrl(doc.id)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#002FA7]" data-testid={`download-doc-${doc.id}`}>
                            <Upload size={16} className="rotate-180" />
                          </Button>
                        </a>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          onClick={async () => {
                            try {
                              await deleteDocument(doc.id);
                              setDocuments(documents.filter(d => d.id !== doc.id));
                              toast.success('Document deleted');
                            } catch (error) { toast.error(formatApiError(error)); }
                          }}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <Card className="border-zinc-200 rounded-sm">
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Notes</CardTitle>
              <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#002FA7] text-white rounded-sm">
                    <Plus size={16} className="mr-2" /> Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateNote} className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase">Note *</Label>
                      <Textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} required className="rounded-sm" rows={4} />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>Cancel</Button>
                      <Button type="submit" className="bg-[#002FA7] text-white">Add Note</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {notes.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">No notes added</div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {notes.map(note => (
                    <div key={note.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-zinc-100 rounded-sm">
                          <Note size={16} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                          <p className="text-xs text-zinc-400 mt-2">
                            By {note.created_by_name || users[note.created_by] || 'Unknown'} • {formatDate(note.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>

        {/* Family Members Sidebar */}
        {families.length > 0 && (
          <div className="w-full lg:w-64 shrink-0" data-testid="family-sidebar">
            {families.map(family => (
              <Card key={family.id} className="border-zinc-200 rounded-sm lg:sticky lg:top-6">
                <CardHeader className="p-4 pb-2 border-b border-zinc-100">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <UsersThree size={16} className="text-[#002FA7]" />
                    {family.name || 'Family'}
                  </CardTitle>
                  {Number(family.family_aum) > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">AUM: {formatCurrency(Number(family.family_aum))}</p>
                  )}
                </CardHeader>
                <CardContent className="p-2">
                  {/* Head */}
                  {family.head_client && (
                    <button
                      onClick={() => { if (family.head_client.id !== id) navigate(`/clients/${family.head_client.id}`); }}
                      className={`w-full text-left p-2 rounded-sm flex items-center gap-3 transition-colors ${family.head_client.id === id ? 'bg-[#002FA7]/10 border border-[#002FA7]/20' : 'hover:bg-zinc-50 cursor-pointer'}`}
                      data-testid={`family-member-${family.head_client.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#002FA7] text-white text-xs flex items-center justify-center font-semibold shrink-0">
                        {(family.head_client.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{family.head_client.full_name}</p>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Head</p>
                      </div>
                    </button>
                  )}
                  {/* Members */}
                  {(family.members || []).map(member => member.client && (
                    <button
                      key={member.client.id}
                      onClick={() => { if (member.client.id !== id) navigate(`/clients/${member.client.id}`); }}
                      className={`w-full text-left p-2 rounded-sm flex items-center gap-3 transition-colors ${member.client.id === id ? 'bg-[#002FA7]/10 border border-[#002FA7]/20' : 'hover:bg-zinc-50 cursor-pointer'}`}
                      data-testid={`family-member-${member.client.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-zinc-200 text-zinc-600 text-xs flex items-center justify-center font-semibold shrink-0">
                        {(member.client.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{member.client.full_name}</p>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{member.relationship_to_head}</p>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* SIP Delete Dialog */}
      <AlertDialog open={!!deleteSIPId} onOpenChange={() => setDeleteSIPId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SIP</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this SIP entry? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSIP} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-sip">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post-Activity Follow-up/Task Prompt */}
      <Dialog open={!!postActivityAction} onOpenChange={(open) => { if (!open) { setPostActivityAction(null); setPostActionType(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {postActivityAction?.type} {postActivityAction?.type === 'Call' ? 'Connected' : 'Completed'} — Next Step?
            </DialogTitle>
          </DialogHeader>
          <PostActivityForm
            postActionType={postActionType}
            setPostActionType={setPostActionType}
            onSkip={() => { setPostActivityAction(null); setPostActionType(null); }}
            onCreate={handlePostActivityCreate}
            mappedEmployees={mappedEmployees}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
